<script lang="ts">
import { stripSiteDomain } from '$lib/utils/url.js';
import Link from '../Link/Link.svelte';

interface ServiceItem {
    name: string;
    description: string;
}

interface Props {
    title?: string;
    headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    titleSize?: string;
    linkText?: string;
    linkUrl?: string;
    items?: ServiceItem[];
    hideBlock?: boolean;
}

let {
    title = '',
    headingLevel = 'h2',
    titleSize = 'h2',
    linkText = '',
    linkUrl = '',
    items = [],
    hideBlock = false,
}: Props = $props();

// Transform URL from Builder.io
const transformedLinkUrl = $derived(stripSiteDomain(linkUrl));
const titleSizeClassName = $derived(`title--${titleSize}`);
</script>

{#if !hideBlock}
    <div class="services">
        <div class="intro">
            {#if title}
                <svelte:element this={headingLevel} class="title {titleSizeClassName}">{title}</svelte:element>
            {/if}
            {#if linkText && transformedLinkUrl}
                <Link to={transformedLinkUrl} class="button button--inverse">
                    {#snippet text()}
                                    {linkText}
                                {/snippet}
                </Link>
            {/if}
        </div>

        <div class="items">
            {#each items as item, index (index)}
                <div class="service" data-aos="fade-up">
                    <h3 class="name">{item.name}</h3>
                    <p class="description">{item.description}</p>
                </div>
            {/each}
        </div>
    </div>
{/if}

<style lang="scss">
    @use '/src/scss/global-constants/typography';
    @use 'sass-mq/mq' as *;

    .services {
        display: flex;
        flex-direction: column;
        gap: 4.8rem;
        margin: 0 var(--layout-pagegrid-margin) 5.6rem;

        @include mq($from: tabletLarge) {
            flex-direction: row;
            justify-content: space-between;
            margin-bottom: 13rem;
        }
    }

    .intro {
        flex: 0 0 40%;
    }

    .items {
        flex: 0 0 40%;
        display: flex;
        flex-direction: column;
        gap: 4.8rem;
    }

    .title {
        color: var(--colour-dreamless-sleep);
        margin-bottom: 1.6rem;
        max-width: 16ch;
        text-wrap: balance;

        @include mq($from: tablet) {
            margin-bottom: 2.4rem;
        }

        &--h1   { @include typography.display-xl; }
        &--h1-5 { @include typography.display-hero; }
        &--h2   { @include typography.display-lg; }
        &--h3   { @include typography.display-md; }
        &--h4   { @include typography.heading-lg; }
        &--h5   { @include typography.heading-md; }
    }

    .name {
        color: var(--colour-dreamless-sleep);
        margin-bottom: 1.6rem;
        @include typography.text-list;
    }

    .description {
        color: var(--colour-squant);
        @include typography.label-sm;
    }
</style>
